import { useCallback, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { resolvePlayerKind } from '@apm/bloom-receiver-sdk';

import { playbackBuffers } from '../playback-buffers.js';

/**
 * masthead 프리뷰 (v32 · `docs/63` §5 · 절대 규칙 82). **`onair/use-onair-player.ts` 의 모양이다.**
 *
 * ── 리시버가 아니다 ─────────────────────────────────────────
 *
 * 리시버로 틀면 `start` 이벤트 · `WatchProgress` 보고 · `useTv2m` 의 트리거 셋이 따라오고
 * **셋 다 「보고 있다」의 증거로 집계된다.** 음소거 프리뷰를 30초 봤다고 3회 12:33 이 되면
 * 안 된다. 그래서 여기는 hls.js 하나와 `<video>` 하나다 — `packages/bloom-receiver-sdk` 에서
 * 가져오는 것은 `resolvePlayerKind()` 뿐이다(ONAIR 와 같은 줄 · 「hls.js 인가 네이티브인가」가
 * 두 벌이 되면 아이폰에서만 갈린다).
 *
 * ── `<video>` 를 새로 만들지 않는다 ───────────────────────────
 *
 * **앱이 들고 있는 한 쌍 중 하나**(`playbackBuffers()[0]`)를 빌린다. 홈이 서 있는 동안
 * `WatchScreen` 은 없어 그 쌍이 비어 있고, OK 뒤 어댑터가 둘을 입양한다 — 그 사이 `halt()`
 * 가 src 를 지운다. 셋째 엘리먼트를 만들면 한 쌍의 이유(WebKit 의 엘리먼트 단위 허가)와
 * 무관한 것이 생기고 ONAIR 선례와 두 벌이 된다(`CLAUDE.md` 의 그 금지).
 *
 * ── 언제나 muted 다 ─────────────────────────────────────────
 *
 * 속성과 프로퍼티 둘 다. 이 파일에 `muted = false` 는 없다(`eslint` 가 막는다) — 소리를
 * 켜는 유일한 줄은 `unlockBuffers()` 이고 OK 핸들러가 제스처 안에서 부른다.
 *
 * ── 실패는 전부 포스터다 ─────────────────────────────────────
 *
 * `play()` 의 거절(`NotAllowedError` · 코덱 · 저장 데이터)도, hls.js 의 치명 오류도, `ended` 도
 * 화면에서는 포스터 하나다. 오류 화면이 없고 재시도도 없다 — 프리뷰는 그림이지 방송이 아니다.
 * `items[0]` 이 끝나면 **다음 항목으로 넘어가지 않는다**(§11-2) — 연속 재생은 리시버의 일이다.
 */
export type PreviewState = 'idle' | 'playing' | 'denied' | 'ended' | 'failed';

/**
 * 「켤지 말지」의 사실을 읽는다. 판정(`previewAllowed`)은 순수하고 여기는 읽는 자리다.
 * `navigator.connection` 은 표준이 아니라 타입에 없다 — 있으면 읽고 없으면 거짓이다.
 */
export function readSaveData(): boolean {
  if (typeof navigator === 'undefined') return false;
  const connection = (navigator as { connection?: { saveData?: boolean } }).connection;
  return connection?.saveData === true;
}

export function useHomePreview(options: {
  container: HTMLElement | null;
  /** `null` 이면 프리뷰가 없다(포스터) */
  url: string | null;
  /** hls.js 의 `startPosition`. **청크 정렬을 하지 않는다**(D16 · 규칙 47 의 것은 리시버다) */
  startSec: number;
}): { state: PreviewState; halt: () => void } {
  const { container, url, startSec } = options;
  const [state, setState] = useState<PreviewState>('idle');
  /** 정리 함수. OK 핸들러가 **페이지 전환 전에** 부른다(`docs/63` §3.3 ②) — 두 번 불려도 된다 */
  const haltRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (container === null || url === null) {
      setState('idle');
      return;
    }

    // ★ **앱이 들고 있는 엘리먼트를 빌린다** — 새로 만들지 않는다(위 ★★).
    const [el] = playbackBuffers();
    el.muted = true;
    el.setAttribute('muted', '');
    el.playsInline = true;
    el.preload = 'auto';
    el.loop = false;
    // 하네스가 「프리뷰 = 한 쌍의 그 엘리먼트」를 재는 축. 정리에서 지운다 — 어댑터가 입양한 뒤
    // 리시버의 엘리먼트에 이 이름이 남으면 안 된다.
    el.dataset['testid'] = 'masthead-preview';
    el.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;' +
      'object-fit:cover;background:#000;opacity:1;z-index:1;';
    container.appendChild(el);

    const listeners = new AbortController();
    el.addEventListener(
      'playing',
      () => {
        setState('playing');
      },
      { signal: listeners.signal },
    );
    el.addEventListener(
      'ended',
      () => {
        // **끝나면 포스터.** `items[1]` 로 넘어가지 않는다 — 여기에 「다음 항목」은 없다.
        el.pause();
        setState('ended');
      },
      { signal: listeners.signal },
    );
    el.addEventListener(
      'error',
      () => {
        setState('failed');
      },
      { signal: listeners.signal },
    );
    setState('idle');

    let hls: Hls | null = null;
    if (resolvePlayerKind() === 'hls') {
      hls = new Hls({ enableWorker: true, startPosition: startSec > 0 ? startSec : -1 });
      hls.on(Hls.Events.ERROR, (_event: unknown, data: { fatal: boolean }) => {
        // 치명적인 것만. 재시도하지 않는다 — 프리뷰는 그림이지 방송이 아니다.
        if (!data.fatal) return;
        setState('failed');
        hls?.destroy();
        hls = null;
      });
      hls.attachMedia(el);
      hls.loadSource(url);
    } else {
      // 아이폰 — 네이티브 HLS. 시작점은 메타데이터가 온 뒤에 준다.
      el.src = url;
      if (startSec > 0) {
        el.addEventListener(
          'loadedmetadata',
          () => {
            el.currentTime = startSec;
          },
          { once: true, signal: listeners.signal },
        );
      }
    }

    // **거절 = 포스터.** `catch` 를 빠뜨리면 masthead 가 검은 사각형으로 남고 TV 에는 콘솔이 없다.
    void Promise.resolve(el.play()).catch(() => {
      setState('denied');
    });

    let halted = false;
    const halt = () => {
      if (halted) return;
      halted = true;
      listeners.abort();
      hls?.destroy();
      hls = null;
      el.pause();
      el.removeAttribute('src');
      el.load();
      delete el.dataset['testid'];
      // **DOM 에서 떼지 않는다** — 허가가 엘리먼트에 붙어 있고, 다음 어댑터가 `appendChild` 로
      // 자기 자리로 옮겨 간다(`use-onair-player.ts` · `hls-player.ts` 와 같은 방침).
      el.muted = true;
    };
    haltRef.current = halt;
    return halt;
  }, [container, url, startSec]);

  const halt = useCallback(() => {
    haltRef.current();
  }, []);

  return { state, halt };
}
